import { Box, Button, CircularProgress, Grid, InputAdornment, TextField, Tooltip, Typography } from "@mui/material";
import PhotoCameraOutlinedIcon from "@mui/icons-material/PhotoCameraOutlined";
import Webcam from "react-webcam";
import { createWorker } from "tesseract.js";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import AlertMessages from "common/Alert/AlertMessages";
import { useSnackbar } from "notistack";
import SimpleModal from "common/Form/Form";
import { useNavigate, useParams } from "react-router-dom";
import ComponentHeaderTwo from "common/ComponentHeaderTwo/ComponentHeaderTwo";
import { ReactComponent as Vehicle } from "../../assets/SideBarIcons/vehicle.svg";
import { checkPrivileges } from "Helpers/CheckPrivileges";
import { useEffect, useRef, useState } from "react";
import {
  GetModelEnginesByModelResponse,
  GetModelsByMakeResponse,
  GetVehicleByIdResponse,
  GetVehicleByVinResponse,
  VehicleByVinResponse,
  VehicleRequest,
} from "subModule/src/services/vehicle/interface";
import Loader from "layout/Loader";
import DynamicSelect from "common/Select/Select";
import BasicDatePicker from "common/Pickers/Pickers";
import { useGetAccountsDropdownService, useGetClientsDropdownService, useGetDriversService } from "subModule/src/services/client/useClient";
import { VehicleProfileService } from "subModule/src/core/services";
import { useGetMakeDropdownService } from "subModule/src/services/make/useMake";
import { useGetVehicleTypeDropdownService } from "subModule/src/services/vehicletype/useVehicleType";
import CloseIcon from "@mui/icons-material/Close";
import FormDialog from "common/Modal/Modal";
import PlateNumberModal from "./PlateNumberModal";
import { useSelector } from "react-redux";
import { selectUserInfo } from "features/user/userSlice";
import { useGetStateService } from "subModule/src/services/state/useState";
import { themes } from "theme/themes";
import { brandingInfo } from "features/server/brandingSlice";
import dayjs from "dayjs";
import { ReactComponent as ModalBtn } from "assets/Modal/ModalBtn.svg";
import CustomersModal from "pages/Customer/CustomersModal";
import DriversModal from "pages/Driver/DriversModal";
import { useGetInsuranceCompaniesDropdownService } from "subModule/src/services/insurance/useInsurance";
import VehicleTabs from "./VehicleTabs";
import { checkSubscriptions } from "Helpers/CheckSubscriptions";
import { useBusinessSetupEntityBreadcrumbs } from "Helpers/useBusinessSetupEntityBreadcrumbs";
interface FormData {
  [key: string]: any;
}
export default function VehicleModal() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const vinWebcamRef = useRef<Webcam>(null);
  const vinImageRef = useRef<HTMLInputElement>(null);
  const [openVinScanner, setOpenVinScanner] = useState<boolean>(false);
  const [showVinCamera, setShowVinCamera] = useState<boolean>(false);
  const [vinScanning, setVinScanning] = useState<boolean>(false);
  const [vinDecoding, setVinDecoding] = useState<boolean>(false);
  const branding = useSelector(brandingInfo);
  const themeColors =
    branding?.attributes?.themeId === 100
      ? branding?.attributes?.customTheme
      : (themes as any)[branding?.attributes?.themeId || 3];
  const {
    register,
    control,
    handleSubmit,
    formState: { errors, dirtyFields, isDirty },
    reset,
    watch,
    setValue,
    trigger
  } = useForm<FormData>({ defaultValues: {
    customerType: { value: 'Individual', label: t('Individual') }
  } });
  const { breadcrumbItems, captureLabel } = useBusinessSetupEntityBreadcrumbs({
    sectionLabelKey: "vehicles",
    moduleLabelKey: "vehiclesList",
    moduleHref: "/vehicles",
    entityFallbackLabelKey: "plateNumber",
    isEdit: !!params?.id,
  });
  const [loading, setLoading] = useState<boolean>(params?.id ? true : false);
  const [IsBtnEnabled, setIsBtnEnabled] = useState<boolean>(
    params?.id ? false : true
  );
  const [openPlateNumberDetails, setOpenPlateNumberDetails] =
    useState<boolean>(false);
  const [models, setModels] = useState<any>([]);
  const [modelEngines, setModelEngines] = useState<any>([]);
  const [updateModal, setUpdateModal] = useState<boolean>(false);
  const [vehicle, setVehicle] = useState<any>({});
  const [addNew, setAddNew] = useState<string>("");
  const [bfmc000UpdateLock, setBfmc000UpdateLock] = useState<boolean | "released">(false);
  const [dropdownSearchTerm, setDropdownSearchTerm] = useState<any>('');
  const [pageNo, setPageNo] = useState<number>(1);
  const [searchLoading, setSearchLoading] = useState<string | null>(null);
  const { data: clientsData, refetch: clientsRefetch } = watch('accountId')?.value 
    ? useGetDriversService({ pageNumber: pageNo, size: 50, active: true, type: [160,180], name: dropdownSearchTerm}, watch('accountId')?.value)
    : useGetClientsDropdownService({ pageNumber: pageNo, size: 50, type: [170], search: dropdownSearchTerm });
  const { data: accountsData, refetch: accountsRefetch } =
    useGetAccountsDropdownService({ pageNumber: pageNo, size: 50, search: dropdownSearchTerm });
  const { data: makesData, refetch: makesRefetch } = useGetMakeDropdownService({
    pageNumber: pageNo,
    size: 50,
    title: dropdownSearchTerm
  });
  const { data: vehicleTypesData, refetch: vehicleTypesRefetch } =
    useGetVehicleTypeDropdownService({ pageNumber: pageNo, size: 50, title: dropdownSearchTerm });
  const { data: statesData, refetch: statesRefetch } = useGetStateService({
    pageNumber: pageNo,
    size: 50,
    name: dropdownSearchTerm
  });
  const statesList = (statesData?.pages[0]?.data?.content || []).map(
    (item: { id?: number; name?: string }) => ({
      value: item.id,
      label: item.name,
    })
  );
  const { data: insuranceData, refetch: insuranceRefetch } =
    useGetInsuranceCompaniesDropdownService({ pageNumber: pageNo, size: 50, title: dropdownSearchTerm });
  const clients = (clientsData?.pages[0]?.data?.content || []).map(
    (item: { id?: number; name?: string }) => ({
      value: item.id,
      label: item.name,
    })
  );
  const accounts = (accountsData?.pages[0]?.data?.content || []).map(
    (item: { id?: number; title?: string | null }) => ({
      value: item.id,
      label: item?.title || item.id,
    })
  );
  const makes = (makesData?.pages[0]?.data?.content || []).map(
    (item: { id: number; title: string }) => ({
      value: item.id,
      label: item.title,
    })
  );
  const vehicleTypes = (vehicleTypesData?.pages[0]?.data?.content || []).map(
    (item?: { id: number; title: string }) => ({
      value: item?.id,
      label: item?.title,
    })
  );
  const insuranceCompanies = (insuranceData?.pages[0]?.data?.content || []).map(
    (item?: { id?: number; title?: string }) => ({
      value: item?.id,
      label: item?.title,
    })
  );
  const customerTypes = [
    { value: 'Individual', label: t('Individual') },
    { value: 'company', label: t('company') }
  ];
  const transmissions = [
    { value: 1, label: t("manual") },
    { value: 2, label: t("automatic") },
  ];
  const fuelTypes = [
    { value: 1, label: t("PETROL") },
    { value: 2, label: t("DIESEL") },
    { value: 3, label: t("ETHANOL") },
    { value: 4, label: t("BIODIESEL") },
    { value: 5, label: t("CNG") },
    { value: 6, label: t("LPG") },
    { value: 7, label: t("HYDROGEN") },
    { value: 8, label: t("HYBRID") },
    { value: 9, label: t("PHEV") },
    { value: 10, label: t("ELECTRIC") },
    { value: 11, label: t("others") },
  ];
  const registrationStatuses = [
    { value: 1, label: t("active") },
    { value: 2, label: t("expired") },
  ];
  const insuranceStatuses = [
    { value: 1, label: t("active") },
    { value: 2, label: t("expired") },
  ];
  const loggedInUserData = useSelector(selectUserInfo);
  const primaryLanguage = loggedInUserData?.business?.primaryLanguage;
  const secondaryLanguage = loggedInUserData?.business?.secondaryLanguage;
  const isArabicLanguage = (lang?: string) => {
    const l = (lang || "").toLowerCase();
    return l === "arabic" || l === "ar" || l.includes("arab");
  };
  const localizedPlateNumberPlaceholder = (lang?: string) =>
    isArabicLanguage(lang) ? t("phPlateNumberAr") : t("phPlateNumberEn");
  const vehiclePlateFormat = loggedInUserData?.business?.vehiclePlateFormat?.formats || [{ title: 'Plate Number', type: 'text', min: 1, max: 15 }];
  useEffect(() => {
    clientsRefetch();
    accountsRefetch();
    makesRefetch();
    vehicleTypesRefetch();
    statesRefetch();
    insuranceRefetch();
  }, []);
  useEffect(() => {
    if (params?.id && clientsData && accountsData) {
      if (loading) getByID();
    }
  }, [params, clientsData, accountsData]);
  useEffect(() => {
    setTimeout(() => clientsRefetch(), 400);
  }, [watch('accountId')?.value]);
  // BFMC lock: re-run when clients/vehicle/loading change (not on every bfmc000UpdateLock flip).
  useEffect(() => {
    if (!params?.id) {
      setBfmc000UpdateLock(false);
      return;
    }
    if (loading || bfmc000UpdateLock === "released") return;
    if (String(vehicle?.id) !== String(params.id)) return;
    const aid = vehicle?.accountId;
    const uid = vehicle?.userId;
    if (aid == null) return;
    if (
      (clientsData?.pages ?? []).some((p: any) =>
        (p?.data?.content ?? []).some(
          (row: any) =>
            String(row?.account?.contractNumber ?? row?.contractNumber ?? "").toLowerCase() === "bfmc000" &&
            (row?.accountId === aid || row?.account?.id === aid || row?.id === uid)
        )
      )
    )
      setBfmc000UpdateLock(true);
  }, [clientsData, params?.id, loading, vehicle]);
  useEffect(() => {
    if (isDirty && params?.id) setIsBtnEnabled(true);
  }, [isDirty, dirtyFields]);
  const fetchModels = (make: any) => {
    VehicleProfileService.getModelsByMake({ page: pageNo, size: 50 }, make)
      .then((response: GetModelsByMakeResponse) => {
        if (response.success) {
          setModels(response?.data || {});
        } else {
          enqueueSnackbar(<AlertMessages text={response.message} />, {
            variant: "error",
          });
        }
      })
      .catch((err: any) => {
        console.error("===", err);
      }).finally(() => setSearchLoading(null));
  };
  const fetchModelEngines = (model: any) => {
    VehicleProfileService.getModelEnginesByModel({ page: pageNo, size: 50 }, model)
      .then((response: GetModelEnginesByModelResponse) => {
        if (response.success) {
          setModelEngines(response?.data || {});
        } else {
          enqueueSnackbar(<AlertMessages text={response.message} />, {
            variant: "error",
          });
        }
      })
      .catch((err: any) => {
        console.error("===", err);
      });
  };
  const getByID = () => {
    VehicleProfileService.getVehicleByID(+(params as any)?.id)
      .then((res: GetVehicleByIdResponse) => {
        if (res.success) {
          captureLabel(res?.data?.plateNumber);
          setBfmc000UpdateLock(false);
          reset({
            ...(res?.data || {}),
            userId: { value: res.data?.userId, label: res.data?.user?.name },
            customerType: res?.data?.accountId ? { value: 'company', label: t('company') } : { value: 'Individual', label: t('Individual') },
            accountId: res.data?.accountId ? { value: res.data?.accountId, label: res.data?.accountTitle } : null,
            vehicleTypeId: {
              value: res.data?.vehicleTypeId,
              label: res?.data?.vehicleTypeTitle,
            },
            makeId: { value: res.data?.makeId, label: res?.data?.makeTitle },
            modelId: { value: res.data?.modelId, label: res?.data?.modelTitle },
            modelEngineId: res.data?.modelEngineId ? {
              value: res.data?.modelEngineId || null,
              label: res?.data?.modelEngineTitle || "",
            } : null,
            year:
              res.data?.year && res.data?.year !== "0"
                ? { value: res.data?.year, label: res?.data?.year }
                : null,
            transmission: res.data?.transmission ? {
              value: res.data?.transmission || 0,
              label:
                transmissions[
                  transmissions.findIndex(
                    (v) => v.value === res.data.transmission
                  )
                ]?.label || "",
            } : null,
            fuelType: res.data?.fuelType ? {
              value: res.data?.fuelType || 0,
              label:
                fuelTypes[
                  fuelTypes.findIndex((v) => v.value === res.data.fuelType)
                ]?.label || "",
            } : null,
            registrationStatus: res.data?.registrationStatus ? {
              value: res.data?.registrationStatus || 0,
              label:
                registrationStatuses[
                  registrationStatuses.findIndex(
                    (v) => v.value === res.data.registrationStatus
                  )
                ]?.label || "",
            } : null,
            insuranceStatus: res.data?.insuranceStatus ? {
              value: res.data?.insuranceStatus || 0,
              label:
                insuranceStatuses[
                  insuranceStatuses.findIndex(
                    (v) => v.value === res.data.insuranceStatus
                  )
                ]?.label || "",
            } : null,
            stateId: res.data?.stateId ? {
              value: res.data?.stateId || null,
              label: res?.data?.stateName || "",
            } : null,
            insuranceCompanyId: res.data?.insuranceCompanyId ? { value: res.data?.insuranceCompanyId, label: res.data?.insuranceProviderName || res.data?.insuranceCompanyId } : null,
          });
          if(res?.data?.insuranceExpiryDate && (new Date(res?.data?.insuranceExpiryDate) < new Date())) setValue('insuranceStatus', { value: 2, label: t("expired") });
          setVehicle(res?.data);
          setTimeout(() => {
            fetchModels(res.data?.makeId);
          }, 500);
          fetchModelEngines(res.data?.modelId);
          setLoading(false);
        } else {
          enqueueSnackbar(
            <AlertMessages text={res.message || "Something went wrong"} />,
            { variant: "error" }
          );
        }
      })
      .catch((err: any) => {
        console.error("===", err);
      });
  };
  const onSubmit = (data: any) => {
    setIsBtnEnabled(false);
    if (params?.id) {
      const payload = {
        ...data,
        userId: data.userId.value,
        accountId: data?.accountId?.value || null,
        vehicleTypeId: data.vehicleTypeId.value,
        makeId: data.makeId.value,
        modelId: data.modelId.value,
        modelEngineId: data.modelEngineId?.value || null,
        year: data.year?.value || "",
        transmission: data.transmission?.value || 0,
        fuelType: data.fuelType?.value || 0,
        registrationStatus: data.registrationStatus?.value || 0,
        insuranceStatus: data.insuranceStatus?.value || 0,
        stateId: data?.stateId?.value || null,
        insuranceCompanyId: data?.insuranceCompanyId?.value || null,
      };
      delete payload.customerType;
      setVehicle(payload);
      if (vehicle?.jobCardId || vehicle?.workOrderId) {
        setVehicle({ ...payload, updateImpact: true });
        setUpdateModal(true);
      } else {
        updateVehicle({ ...payload, updateImpact: false });
      }
    } else {
      delete data.customerType;
      VehicleProfileService.createVehicle({
        ...data,
        userId: data.userId.value,
        accountId: data?.accountId?.value || null,
        vehicleTypeId: data.vehicleTypeId.value,
        makeId: data.makeId.value,
        modelId: data.modelId.value,
        modelEngineId: data.modelEngineId?.value || null,
        year: data.year?.value || "",
        transmission: data.transmission?.value || 0,
        fuelType: data.fuelType?.value || 0,
        registrationStatus: data.registrationStatus?.value || 0,
        insuranceStatus: data.insuranceStatus?.value || 0,
        stateId: data?.stateId?.value || null,
        insuranceCompanyId: data?.insuranceCompanyId?.value || null,
      })
        .then((response: any) => {
          if (response.success) {
            enqueueSnackbar(
              <AlertMessages
                statusCode={response.status}
                text={
                  t("Vehicle") + " " + t("created") + " " + t("successfully")
                }
              />,
              { variant: "success" }
            );
            navigate(-1);
          } else {
            setIsBtnEnabled(true);
            enqueueSnackbar(<AlertMessages text={t(response?.message)} />, {
              variant: "error",
            });
          }
        })
        .catch((err: any) => {
          console.error("===", err);
          setIsBtnEnabled(true);
          enqueueSnackbar(
            <AlertMessages
              text={
                err?.message || err?.data?.message || "Something went wrong"
              }
            />,
            { variant: "error" }
          );
        });
    }
  };
  const updateVehicle = (payload: VehicleRequest) => {
    VehicleProfileService.updateVehicle(+(params as any)?.id, payload)
      .then((response: any) => {
        if (response.success) {
          captureLabel(payload?.plateNumber);
          getByID();
          enqueueSnackbar(
            <AlertMessages
              statusCode={response.status}
              text={t("Vehicle") + " " + t("updated") + " " + t("successfully")}
            />,
            { variant: "success" }
          );
        } else {
          enqueueSnackbar(<AlertMessages text={t(response?.message)} />, {
            variant: "error",
          });
        }
        setIsBtnEnabled(true);
      })
      .catch((err: any) => {
        console.error("===", err);
        setIsBtnEnabled(true);
        enqueueSnackbar(
          <AlertMessages
            text={err?.message || err?.data?.message || "Something went wrong"}
          />,
          { variant: "error" }
        );
      });
  };
  const extractVinFromText = (text: string) =>
    text.replace(/[^a-zA-Z0-9]/g, "").toUpperCase().match(/[A-HJ-NPR-Z0-9]{17}/)?.[0] || null;
  const scanVinFromImage = async (image: string | HTMLImageElement) => {
    setVinScanning(true);
    try {
      const worker = await createWorker("eng");
      const { data: { text } } = await worker.recognize(image);
      await worker.terminate();
      const vin = extractVinFromText(text);
      if (vin) {
        setValue("vin", vin, { shouldDirty: true, shouldValidate: true });
        trigger("vin");
        if (!IsBtnEnabled) setIsBtnEnabled(true);
        setOpenVinScanner(false);
        setShowVinCamera(false);
      } else {
        enqueueSnackbar(<AlertMessages text={t("Not Found")} />, { variant: "warning" });
      }
    } catch {
      enqueueSnackbar(<AlertMessages text={t("Not Found")} />, { variant: "error" });
    } finally {
      setVinScanning(false);
    }
  };
  const applyVinDecodeData = (data: VehicleByVinResponse) => {
    if (data.vin) setValue("vin", data.vin, { shouldDirty: true });
    if (data.exteriorColor) setValue("color", data.exteriorColor, { shouldDirty: true });
    if (data.measurements) setValue("tyreInformation", data.measurements, { shouldDirty: true });
    if (data.trim) setValue("notes", data.trim, { shouldDirty: true });
    if (data.modelYear && data.modelYear !== "0") {
      setValue("year", { value: data.modelYear, label: data.modelYear }, { shouldDirty: true });
    }
    if (data.transmission) {
      const isAutomatic = /auto/i.test(data.transmission);
      setValue("transmission", {
        value: isAutomatic ? 2 : 1,
        label: t(isAutomatic ? "automatic" : "manual"),
      }, { shouldDirty: true });
    }
    if (data.fuelType) {
      const ftLower = data.fuelType.toLowerCase();
      if (ftLower.includes("gasoline") || ftLower.includes("petrol")) {
        const petrol = fuelTypes.find((f) => f.value === 1);
        if (petrol) setValue("fuelType", petrol, { shouldDirty: true });
      } else {
        const match = fuelTypes.find((f) =>
          ftLower.includes(f.label.toLowerCase()) || f.label.toLowerCase().includes(ftLower)
        );
        if (match) setValue("fuelType", match, { shouldDirty: true });
      }
    }
    if (data.doors) {
      const doors = parseInt(String(data.doors), 10);
      if (!isNaN(doors)) setValue("seatingCapacity", doors, { shouldDirty: true });
    }
    if (data.bodyType) {
      const vehicleType = vehicleTypes.find(
        (v) => v.label?.toLowerCase() === data.bodyType?.toLowerCase()
      );
      if (vehicleType) setValue("vehicleTypeId", vehicleType, { shouldDirty: true });
    }
    if (data.maker) {
      handleDropdownActions("makes", "searchIt", data.maker, (result) => {
        const content = result?.data?.pages?.[0]?.data?.content || [];
        const match = content.find((item: { id: number; title: string }) =>
          item.title?.toLowerCase().includes(data.maker!.toLowerCase()) ||
          data.maker!.toLowerCase().includes(item.title?.toLowerCase() || "")
        );
        if (match) {
          setValue("makeId", { value: match.id, label: match.title }, { shouldDirty: true });
          setValue("modelId", null);
          VehicleProfileService.getModelsByMake({ page: 1, size: 50 }, match.id)
            .then((response: GetModelsByMakeResponse) => {
              if (response.success) {
                setModels(response?.data || {});
                if (data.model) {
                  const model = response?.data?.content?.find(
                    (item) => item.title?.toLowerCase() === data.model?.toLowerCase()
                  );
                  if (model) {
                    setValue("modelId", { value: model.id, label: model.title }, { shouldDirty: true });
                    fetchModelEngines(model.id);
                  }
                }
              }
            }).finally(() => handleDropdownActions("makes", "searchIt", ""));
        }
      });
    }
    if (!IsBtnEnabled) setIsBtnEnabled(true);
  };
  const handleDecodeVin = async () => {
    const isValid = await trigger("vin");
    const vin = watch("vin");
    if (!isValid || !vin) return;
    setVinDecoding(true);
    VehicleProfileService.getVehicleByVin(vin)
      .then((response: GetVehicleByVinResponse) => {
        if (response.success) {
          applyVinDecodeData(response?.data || {});
          enqueueSnackbar(<AlertMessages text={t("vinDecodedSuccessfully")} />, { variant: "success" });
        } else {
          enqueueSnackbar(<AlertMessages text={response.message || t("Not Found")} />, { variant: "error" });
        }
      })
      .catch((err: any) => {
        console.error("===", err);
        enqueueSnackbar(
          <AlertMessages text={err?.message || err?.data?.message || "Something went wrong"} />,
          { variant: "error" }
        );
      })
      .finally(() => setVinDecoding(false));
  };
  const handleDropdownActions = (typeName: string, action: 'searchIt' | 'loadNext', search?: string, onComplete?: (result?: any) => void) => {
    if(action === 'searchIt') {
      setPageNo(1);
      setDropdownSearchTerm(search);
      if(search) setSearchLoading(typeName);
    } else {
      setPageNo(pageNo + 1);
      setSearchLoading(typeName);
    }
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      if(typeName === 'accounts') accountsRefetch().finally(() => setSearchLoading(null));
      else if(typeName === 'customers') clientsRefetch().finally(() => setSearchLoading(null));
      else if(typeName === 'makes') makesRefetch().then((result) => onComplete?.(result)).finally(() => setSearchLoading(null));
      else if(typeName === 'models') {
        VehicleProfileService.getModelsByMake({ page: action === 'loadNext' ? (pageNo + 1) : 1, size: 50, title: action === 'searchIt' ? search : '' }, watch('makeId')?.value)
        .then((response: GetModelsByMakeResponse) => {
          if (response.success) {
            setModels(response?.data);
          } else {
            enqueueSnackbar(<AlertMessages text={response.message} />, {
              variant: "error",
            });
          }
        }).catch((err: any) => {
          console.error("===", err);
        }).finally(() => setSearchLoading(null));
      }
      else if(typeName === 'modelEngines') {
        VehicleProfileService.getModelEnginesByModel({ page: action === 'loadNext' ? (pageNo + 1) : 1, size: 50, title: action === 'searchIt' ? search : '' }, watch('modelId')?.value)
        .then((response: GetModelEnginesByModelResponse) => {
          if (response.success) {
            setModelEngines(response?.data || {});
          } else {
            enqueueSnackbar(<AlertMessages text={response.message} />, {
              variant: "error",
            });
          }
        }).catch((err: any) => {
            console.error("===", err);
          }).finally(() => setSearchLoading(null));
        }
      else if(typeName === 'vehicleTypes') vehicleTypesRefetch().finally(() => setSearchLoading(null));
      else if(typeName === 'states') statesRefetch().finally(() => setSearchLoading(null));
      else if(typeName === 'insuranceCompanies') insuranceRefetch().finally(() => setSearchLoading(null));
    }, 300);    
  }
  return (
    <>
      <ComponentHeaderTwo
        showBtn={true}
        showSaveBtn={true}
        onClick={() => navigate('/vehicles')}
        onSubmit={handleSubmit(onSubmit)}
        heading={params?.id ? t("editVehicles") : t("addVehicle")}
        icon={<Vehicle height={28} width={28} />}
        btnText={"back"}
        btnType={"back"}
        isBtnEnabled={IsBtnEnabled}
        showCreateBtn={checkPrivileges("ROLE_VEHICLE_CREATE")}
        breadcrumbItems={breadcrumbItems}
      />
      <Box className={`${params?.id ? 'flex' : ''}`}>
        {params?.id ? <VehicleTabs params={params} selectedIndex={0} /> : null}
        {loading ? (
          <Loader />
        ) : (
          <SimpleModal
            visable={true}
            title={params?.id ? "updateVehicle" : "addVehicle"}
            isButtonVisable={true}
            isVisableBtn={IsBtnEnabled}
            btnclosetext="Back"
            buttonText={params?.id ? "update" : "submit"}
            attBtnNotshow={false}
            notVisableBackbtn={true}
            showPortList={handleSubmit(onSubmit)}
            formSubmit={handleSubmit(onSubmit)}
            content={
              <Grid
                container
                component="form"
                id="form"
                className="items-baseline"
                spacing={3}
                onSubmit={handleSubmit(onSubmit)}
              >
                <Grid item container xs={12} spacing={3}>
                  <Grid item xs={12} md={3}>
                    <Controller
                      name="customerType"
                      control={control}
                      defaultValue={null}
                      rules={{ required: t("This field is required.") }}
                      render={({ field }) => (
                        <DynamicSelect
                          label={t("customerType") + ' *'}
                          defaultPlaceholder="phSelectCustomerType"
                          {...field}
                          options={customerTypes}
                          isSearchable
                          isDisabled={bfmc000UpdateLock === true}
                          onChange={(selectedOption: any) => {
                            if (params?.id) setBfmc000UpdateLock("released");
                            field.onChange(selectedOption);
                            setValue('accountId', null);
                            setValue('userId', null);
                          }}
                          error={errors.customerType?.message as string}
                        />
                      )}
                    />
                  </Grid>
                  {watch('customerType')?.value === 'company' ? <Grid item xs={12} md={3}>
                    <Controller
                      name="accountId"
                      control={control}
                      defaultValue={null}
                      rules={{ required: t("This field is required.") }}
                      render={({ field }) => (
                        <DynamicSelect
                          label={t("company") + ' *'}
                          defaultPlaceholder="phSelectCompany"
                          {...field}
                          options={accounts}
                          isSearchable
                          isDisabled={bfmc000UpdateLock === true}
                          isLoading={searchLoading === 'accounts'}
                          onChange={(selectedOption: any) => {
                            if (params?.id) setBfmc000UpdateLock("released");
                            field.onChange(selectedOption);
                            setValue('userId', null);
                          }}
                          onInputChange={(search: string, { action }: { action: string }) => {
                            if(action === 'input-change' || 
                              (action !== 'set-value' && action !== 'menu-close' && action !== 'input-blur')
                            ) handleDropdownActions("accounts", 'searchIt', search);
                            else if(action === 'input-blur' || action === 'menu-close') {
                              if(dropdownSearchTerm || pageNo > 1) handleDropdownActions("accounts", 'searchIt', '');
                              else setDropdownSearchTerm('');
                              setPageNo(1);
                            }
                          }}
                          onMenuScrollToBottom={() => {if(!(accountsData?.pages[0]?.data as any).last) handleDropdownActions("accounts", 'loadNext')}}
                          error={errors.accountId?.message as string}
                          page={pageNo}
                        />
                      )}
                    />
                  </Grid> : null}
                  {watch('customerType')?.value === 'Individual' || watch('accountId')?.value ? 
                    <Grid item xs={12} md={3} className="grid" sx={{
                      gridTemplateColumns: checkPrivileges(watch('accountId')?.value ? "ROLE_CLIENT_CREATE" : "ROLE_CUSTOMER_CREATE") ? "85% 10%" : "100%",
                      "& .addNew path": { fill: themeColors?.primary },
                    }}>
                      <Controller
                        name="userId"
                        control={control}
                        defaultValue={null}
                        rules={{ required: t("This field is required.") }}
                        render={({ field }) => (
                          <DynamicSelect
                            label={(watch('accountId')?.value ? t('driver') : t("customer")) + ' *'}
                            defaultPlaceholder={watch('accountId')?.value ? "phSelectDriver" : "phSelectCustomer"}
                            {...field}
                            options={clients}
                            isSearchable
                            isDisabled={bfmc000UpdateLock === true}
                            isLoading={searchLoading === 'customers'}
                            onChange={(selectedOption: any) => {
                              if (params?.id) setBfmc000UpdateLock("released");
                              field.onChange(selectedOption);
                            }}
                            onInputChange={(search: string, { action }: { action: string }) => {
                              if(action === 'input-change' || 
                                (action !== 'set-value' && action !== 'menu-close' && action !== 'input-blur')
                              ) handleDropdownActions("customers", 'searchIt', search);
                              else if(action === 'input-blur' || action === 'menu-close') {
                                if(dropdownSearchTerm || pageNo > 1) handleDropdownActions("customers", 'searchIt', '');
                                else setDropdownSearchTerm('');
                                setPageNo(1);
                              }
                            }}
                            onMenuScrollToBottom={() => {if(!(clientsData?.pages[0]?.data as any).last) handleDropdownActions("customers", 'loadNext')}}
                            error={errors.userId?.message as string}
                            page={pageNo}
                          />
                        )}
                      />
                      {checkPrivileges(watch('accountId')?.value ? "ROLE_CLIENT_CREATE" : "ROLE_CUSTOMER_CREATE") && bfmc000UpdateLock !== true && (
                        <Tooltip
                          onClick={() => setAddNew(watch('accountId')?.value ? "drivers" : "customers")}
                          title={watch('accountId')?.value ? t("addDriver") : t("addCustomer")}
                        >
                          <ModalBtn className="mt-5 mx-2 mb-2 cursor-pointer h-[38px] addNew" />
                        </Tooltip>
                      )}
                      {addNew === "customers" || addNew == "drivers" ? (
                        <FormDialog
                          open={addNew === "customers" || addNew === "drivers"}
                          onClose={(event, reason) =>
                            event && reason !== "backdropClick" ? setAddNew("") : ""
                          }
                          title={watch('accountId')?.value ? t("addDriver") : t("addCustomer")}
                          createPopup={true}
                          fullWidth
                          hideActions={true}
                          content={watch('accountId')?.value ? 
                              <DriversModal
                                setAddNew={setAddNew}
                                driversRefetch={clientsRefetch}
                                accountId={watch('accountId')?.value}
                              />
                            : <CustomersModal
                              setAddNew={setAddNew}
                              customersRefetch={clientsRefetch}
                            />
                          }
                        />
                      ) : null}
                    </Grid> 
                  : null}
                </Grid>
                <Grid item xs={12}>
                  <h1 className='flex font-Saira font-[700] text-[17px]' style={{ color: themeColors?.primary }}>{t('Details')}</h1>
                </Grid>
                <Grid item xs={12} md={3} className="relative">
                  <TextField
                    {...register("plateNumber", {
                      required: t("This field is required."),
                    })}
                    label={`${!primaryLanguage ? t("primary") : ""} ${t("plateNumber")} ${primaryLanguage ? ` (${t(primaryLanguage)})` : ""} *`}
                    placeholder={localizedPlateNumberPlaceholder(primaryLanguage)}
                    title={(`${!primaryLanguage ? t("primary") : ""} ${t("plateNumber")} ${primaryLanguage ? ` (${t(primaryLanguage)})` : ""} *`).replace(/\s+/g, ' ').trim()}
                    fullWidth
                    disabled={bfmc000UpdateLock === true}
                    error={!!errors.plateNumber}
                    helperText={errors.plateNumber?.message as string}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{ readOnly: true }}
                    onClick={() => bfmc000UpdateLock !== true && setOpenPlateNumberDetails(true)}
                    sx={{ '& .Mui-disabled': { opacity: 0.5 } }}
                  />
                  {watch("plateNumber")?.length > 0 && bfmc000UpdateLock !== true && (
                    <CloseIcon
                      className="cursor-pointer absolute top-[52px] right-[8px] w-[20px]"
                      sx={{ "& svg": { color: "#b1b1b1" } }}
                      onClick={() => {
                        setValue("plateNumber", "");
                        trigger('plateNumber');
                        if (!IsBtnEnabled) setIsBtnEnabled(true);
                      }}
                    />
                  )}
                  {openPlateNumberDetails && (
                    <FormDialog
                      open={openPlateNumberDetails}
                      onClose={(event, reason) =>
                        event && reason !== "backdropClick"
                          ? setOpenPlateNumberDetails(false)
                          : ""
                      }
                      title={t("plateNumber") + " " + t("Details")}
                      createPopup={true}
                      fullWidth
                      hideActions={true}
                      content={
                        <PlateNumberModal
                          setOpenPlateNumberDetails={setOpenPlateNumberDetails}
                          setValue={setValue}
                          trigger={trigger}
                          plate={watch("plateNumber")}
                          region={watch("plateRegion")}
                          {...{ setIsBtnEnabled, vehiclePlateFormat }}
                        />
                      }
                    />
                  )}
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    {...register("secondaryPlateNumber")}
                    label={`${!secondaryLanguage ? t("secondary") : ""} ${t("plateNumber")} ${secondaryLanguage ? ` (${t(secondaryLanguage)})` : ""}`}
                    placeholder={localizedPlateNumberPlaceholder(secondaryLanguage)}
                    title={(`${!secondaryLanguage ? t("secondary") : ""} ${t("plateNumber")} ${secondaryLanguage ? ` (${t(secondaryLanguage)})` : ""}`).replace(/\s+/g, ' ').trim()}
                    fullWidth
                    disabled={bfmc000UpdateLock === true}
                    error={!!errors.secondaryPlateNumber}
                    helperText={errors.secondaryPlateNumber?.message as string}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{ dir: "ltr", style: { letterSpacing: "normal" } }}
                    sx={{ '& .Mui-disabled': { opacity: 0.5 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6} sx={{ '& .MuiInputBase-root': { paddingRight: 0 } }}>
                  <TextField
                    {...register("vin", {
                      validate: (value) => !value || /^[a-zA-Z0-9]{17}$/.test(value) || t("vinExactly17Chars"),
                    })}
                    label={t("vin")}
                    placeholder={t("phVIN")}
                    fullWidth
                    disabled={bfmc000UpdateLock === true}
                    error={!!errors.vin}
                    helperText={errors.vin?.message as string}
                    InputLabelProps={{ shrink: true }}
                    onChange={(e) => {
                      if (bfmc000UpdateLock === true) return;
                      const sanitizedValue = e.target.value.replace(
                        /[^a-zA-Z0-9]/g,
                        ""
                      ).slice(0, 17);
                      setValue("vin", sanitizedValue, { shouldDirty: true });
                      trigger('vin');
                    }}
                    InputProps={{
                      endAdornment: bfmc000UpdateLock !== true ? (checkSubscriptions("VIN_DECODER") && 
                        <InputAdornment position="end">
                          <Tooltip title={t("scanVin")}>
                            <PhotoCameraOutlinedIcon
                              className="cursor-pointer"
                              sx={{ color: themeColors?.primary, mr: '8px' }}
                              onClick={() => {
                                setShowVinCamera(false);
                                setOpenVinScanner(true);
                              }}
                            />
                          </Tooltip>
                          <Button
                            size="small"
                            disabled={vinDecoding}
                            onClick={handleDecodeVin}
                            sx={{
                              minWidth: "auto",
                              px: 2.5,
                              py: 0.5,
                              backgroundColor: themeColors?.primary,
                              color: "#fff",
                              fontWeight: 400,
                              textTransform: 'none',
                              "&:hover": { backgroundColor: themeColors?.primary },
                              height: '38px',
                              fontFamily: 'Saira, sans-serif',
                              borderRadius: '0 8px 8px 0',
                            }}
                          >
                            {vinDecoding ? <CircularProgress sx={{ color: '#fff', position: 'absolute', left: 0 }} size={16} /> : null}
                            {t("decode")}
                          </Button>
                        </InputAdornment>
                      ) : undefined,
                    }}
                    sx={{ '& .Mui-disabled': { opacity: 0.5 } }}
                  />
                  {openVinScanner && (
                    <FormDialog
                      open={openVinScanner}
                      onClose={() => {
                        if (!vinScanning) {
                          setOpenVinScanner(false);
                          setShowVinCamera(false);
                        }
                      }}
                      title="scanVinFromImage"
                      hideActions
                      fullWidth
                      maxWidth="sm"
                      content={
                        <Box>
                          {showVinCamera ? (
                            <Box sx={{ height: 300, overflow: "hidden", borderRadius: 1, mb: 2 }}>
                              <Webcam
                                ref={vinWebcamRef}
                                audio={false}
                                screenshotFormat="image/jpeg"
                                videoConstraints={{ facingMode: "environment" }}
                                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                              />
                            </Box>
                          ) : null}
                          <input
                            ref={vinImageRef}
                            type="file"
                            accept="image/*"
                            hidden
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              e.target.value = "";
                              if (!file) return;
                              const img = document.createElement("img");
                              const url = URL.createObjectURL(file);
                              try {
                                await new Promise<void>((resolve, reject) => {
                                  img.onload = () => resolve();
                                  img.onerror = reject;
                                  img.src = url;
                                });
                                await scanVinFromImage(img);
                              } catch {
                                enqueueSnackbar(<AlertMessages text={t("Not Found")} />, { variant: "error" });
                              } finally {
                                URL.revokeObjectURL(url);
                              }
                            }}
                          />
                          {showVinCamera ? (
                            <>
                              <Button
                                fullWidth
                                disabled={vinScanning}
                                onClick={() => {
                                  const image = vinWebcamRef.current?.getScreenshot();
                                  if (image) scanVinFromImage(image);
                                }}
                                sx={{
                                  mb: 2,
                                  backgroundColor: themeColors?.primary,
                                  color: "#fff",
                                  "&:hover": { backgroundColor: themeColors?.primary },
                                }}
                              >
                                {t("takePhoto")}
                              </Button>
                              <Button
                                fullWidth
                                onClick={() => setShowVinCamera(false)}
                                sx={{
                                  mb: 2,
                                  backgroundColor: "#fff",
                                  color: themeColors?.primary,
                                  border: `1px solid ${themeColors?.primary}`,
                                  "&:hover": { backgroundColor: "#fff" },
                                }}
                              >
                                {t("back")}
                              </Button>
                            </>
                          ) : (
                            <Box sx={{ display: "flex", gap: 1, mb: 2, flexDirection: 'column' }}>
                              <Button
                                fullWidth
                                onClick={() => setShowVinCamera(true)}
                                sx={{
                                  backgroundColor: themeColors?.primary,
                                  color: "#fff",
                                  "&:hover": { backgroundColor: themeColors?.primary },
                                }}
                              >
                                {t("takePhoto")}
                              </Button>
                              <Button
                                fullWidth
                                disabled={vinScanning}
                                onClick={() => vinImageRef.current?.click()}
                                sx={{
                                  backgroundColor: 'transparent',
                                  color: themeColors?.primary,
                                  border: '1px solid' + themeColors?.primary,
                                  "&:hover": { backgroundColor: themeColors?.primary, color: '#fff' },
                                }}
                              >
                                {t("selectImage")}
                              </Button>
                            </Box>
                          )}
                          <Typography variant="body2" color="text.secondary">
                            {t("vinScanTip")}
                          </Typography>
                        </Box>
                      }
                    />
                  )}
                </Grid>
                <Grid item xs={12} md={3}>
                  <Controller
                      name="stateId"
                      control={control}
                      defaultValue={null}
                      rules={{ required: false }}
                      render={({ field }) => (
                          <DynamicSelect
                              label={t("state")}
                              defaultPlaceholder="phSelectState"
                              {...field}
                              options={statesList || []}
                              isSearchable
                              isClearable
                              isLoading={searchLoading === 'states'}
                              onChange={(selectedOption: any) => field.onChange(selectedOption)}
                              onInputChange={(search: string, { action }: { action: string }) => {
                                if(action === 'input-change' || 
                                  (action !== 'set-value' && action !== 'menu-close' && action !== 'input-blur')
                                ) handleDropdownActions("states", 'searchIt', search);
                                else if(action === 'input-blur' || action === 'menu-close') {
                                  if(dropdownSearchTerm || pageNo > 1) handleDropdownActions("states", 'searchIt', '');
                                  else setDropdownSearchTerm('');
                                  setPageNo(1);
                                }
                              }}
                              onMenuScrollToBottom={() => {if(!(statesData?.pages[0]?.data as any).last) handleDropdownActions("states", 'loadNext')}}
                              error={errors.stateId?.message as string}
                              page={pageNo}
                          />
                      )}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Controller
                    name="vehicleTypeId"
                    control={control}
                    defaultValue={null}
                    rules={{ required: t("This field is required.") }}
                    render={({ field }) => (
                      <DynamicSelect
                        label={t("vehicleType") + ' *'}
                        defaultPlaceholder="phSelectVehicleType"
                        {...field}
                        options={vehicleTypes}
                        isSearchable
                        isLoading={searchLoading === 'vehicleTypes'}
                        onChange={(selectedOption: any) =>
                          field.onChange(selectedOption)
                        }
                        onInputChange={(search: string, { action }: { action: string }) => {
                          if(action === 'input-change' || 
                            (action !== 'set-value' && action !== 'menu-close' && action !== 'input-blur')
                          ) handleDropdownActions("vehicleTypes", 'searchIt', search);
                          else if(action === 'input-blur' || action === 'menu-close') {
                            if(dropdownSearchTerm || pageNo > 1) handleDropdownActions("vehicleTypes", 'searchIt', '');
                            else setDropdownSearchTerm('');
                            setPageNo(1);
                          }
                        }}
                        onMenuScrollToBottom={() => {if(!(vehicleTypesData?.pages[0]?.data as any).last) handleDropdownActions("vehicleTypes", 'loadNext')}}
                        error={errors.vehicleTypeId?.message as string}
                        page={pageNo}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Controller
                    name="makeId"
                    control={control}
                    defaultValue={null}
                    rules={{ required: t("This field is required.") }}
                    render={({ field }) => (
                      <DynamicSelect
                        label={t("Make") + ' *'}
                        defaultPlaceholder="phSelectMake"
                        {...field}
                        options={makes}
                        isSearchable
                        isLoading={searchLoading === 'makes'}
                        onChange={(selectedOption: any) => {
                          field.onChange(selectedOption);
                          fetchModels(selectedOption.value);
                          setValue("modelId", null);
                          setValue("modelEngineId", null);
                        }}
                        onInputChange={(search: string, { action }: { action: string }) => {
                          if(action === 'input-change' || 
                            (action !== 'set-value' && action !== 'menu-close' && action !== 'input-blur')
                          ) handleDropdownActions("makes", 'searchIt', search);
                          else if(action === 'input-blur' || action === 'menu-close') {
                            if(dropdownSearchTerm || pageNo > 1) handleDropdownActions("makes", 'searchIt', '');
                            else setDropdownSearchTerm('');
                            setPageNo(1);
                          }
                        }}
                        onMenuScrollToBottom={() => {if(!(makesData?.pages[0]?.data as any).last) handleDropdownActions("makes", 'loadNext')}}
                        error={errors.makeId?.message as string}
                        page={pageNo}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Controller
                    name="modelId"
                    control={control}
                    defaultValue={null}
                    rules={{ required: t("This field is required.") }}
                    render={({ field }) => (
                      <DynamicSelect
                        label={t("Model") + ' *'}
                        defaultPlaceholder="phSelectModel"
                        {...field}
                        options={models?.content?.map((item: { id: number; title: string }) => ({ value: item.id, label: item.title, })) || []}
                        isSearchable
                        isDisabled={!watch("makeId")?.value}
                        isLoading={searchLoading === 'models'}
                        onChange={(selectedOption: any) => {
                          field.onChange(selectedOption);
                          setTimeout(() => {
                            fetchModelEngines(selectedOption.value);
                          }, 1000);
                          setValue("modelEngineId", null);
                        }}
                        onInputChange={(search: string, { action }: { action: string }) => {
                          if(action === 'input-change' || 
                            (action !== 'set-value' && action !== 'menu-close' && action !== 'input-blur')
                          ) handleDropdownActions("models", 'searchIt', search);
                          else if(action === 'input-blur' || action === 'menu-close') {
                            if(dropdownSearchTerm || pageNo > 1) handleDropdownActions("models", 'searchIt', '');
                            else setDropdownSearchTerm('');
                            setPageNo(1);
                          }
                        }}
                        onMenuScrollToBottom={() => {if(!models?.last) handleDropdownActions("models", 'loadNext')}}
                        error={errors.modelId?.message as string}
                        page={pageNo}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Controller
                    name="modelEngineId"
                    control={control}
                    defaultValue={null}
                    rules={{ required: false }}
                    render={({ field }) => (
                      <DynamicSelect
                        label={t("modelEngine")}
                        defaultPlaceholder="phSelectModelEngine"
                        {...field}
                        options={modelEngines?.content?.map((item: { id: number; title: string }) => ({ value: item.id, label: item.title, })) || []}
                        isSearchable
                        isDisabled={
                          !watch("makeId")?.value || !watch("modelId")?.value
                        }
                        onChange={(selectedOption: any) =>
                          field.onChange(selectedOption)
                        }
                        error={errors.modelEngineId?.message as string}
                        isLoading={searchLoading === 'modelEngines'}
                        onInputChange={(search: string, { action }: { action: string }) => {
                          if(action === 'input-change' || 
                            (action !== 'set-value' && action !== 'menu-close' && action !== 'input-blur')
                          ) handleDropdownActions("modelEngines", 'searchIt', search);
                          else if(action === 'input-blur' || action === 'menu-close') {
                            if(dropdownSearchTerm || pageNo > 1) handleDropdownActions("modelEngines", 'searchIt', '');
                            else setDropdownSearchTerm('');
                            setPageNo(1);
                          }
                        }}
                        onMenuScrollToBottom={() => {if(!modelEngines?.last) handleDropdownActions("modelEngines", 'loadNext')}}
                        page={pageNo}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Controller
                    name="year"
                    control={control}
                    defaultValue={null}
                    rules={{ required: false }}
                    render={({ field }) => (
                      <DynamicSelect
                        label={t("year")}
                        defaultPlaceholder="phSelectYear"
                        {...field}
                        options={Array.from({ length: 60 }, (_, i) => ({
                          label: new Date().getFullYear() - i,
                          value: new Date().getFullYear() - i,
                        }))}
                        isSearchable
                        onChange={(selectedOption: any) =>
                          field.onChange(selectedOption)
                        }
                        error={errors.year?.message as string}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Controller
                    name="transmission"
                    control={control}
                    defaultValue={null}
                    rules={{ required: false }}
                    render={({ field }) => (
                      <DynamicSelect
                        label={t("transmissionType")}
                        defaultPlaceholder="phSelectTransmission"
                        {...field}
                        options={transmissions}
                        isSearchable
                        onChange={(selectedOption: any) =>
                          field.onChange(selectedOption)
                        }
                        error={errors.transmission?.message as string}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Controller
                    name="fuelType"
                    control={control}
                    defaultValue={null}
                    rules={{ required: false }}
                    render={({ field }) => (
                      <DynamicSelect
                        label={t("fuelType")}
                        defaultPlaceholder="phSelectFuelType"
                        {...field}
                        options={fuelTypes}
                        isSearchable
                        onChange={(selectedOption: any) =>
                          field.onChange(selectedOption)
                        }
                        error={errors.fuelType?.message as string}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    {...register("color")}
                    label={t("color")}
                    placeholder={t("phVehicleColor")}
                    title={t("color")}
                    fullWidth
                    error={!!errors.color}
                    helperText={errors.color?.message as string}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    type="number"
                    {...register("odometer")}
                    onChange={(e: any) => {
                      const value = e.target.value;
                      e.target.value = value < 0 ? 0 : value || "";
                      if (!IsBtnEnabled) setIsBtnEnabled(true);
                    }}
                    label={t("odometer")}
                    placeholder={t("phOdometer")}
                    title={t("odometer")}
                    fullWidth
                    error={!!errors.odometer}
                    helperText={errors.odometer?.message as string}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    type="number"
                    {...register("dailyMileage")}
                    onChange={(e: any) => {
                      const value = e.target.value;
                      e.target.value = value < 0 ? 0 : value || "";
                      if (!IsBtnEnabled) setIsBtnEnabled(true);
                    }}
                    label={t("avgDailyMileage")}
                    placeholder={t("phAvgDailyMileage")}
                    title={t("avgDailyMileage")}
                    fullWidth
                    error={!!errors.dailyMileage}
                    helperText={errors.dailyMileage?.message as string}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    type="number"
                    {...register("avgDailyFuelConsumption")}
                    onChange={(e: any) => {
                      const value = e.target.value;
                      e.target.value = value < 0 ? 0 : value || "";
                      if (!IsBtnEnabled) setIsBtnEnabled(true);
                    }}
                    label={t("avgDailyFuelConsumption")}
                    placeholder={t("phAvgDailyFuel")}
                    title={t("avgDailyFuelConsumption")}
                    fullWidth
                    error={!!errors.avgDailyFuelConsumption}
                    helperText={errors.avgDailyFuelConsumption?.message as string}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    type="number"
                    {...register("seatingCapacity")}
                    onChange={(e: any) => {
                      const value = e.target.value;
                      e.target.value = value < 0 ? 0 : value || "";
                      if (!IsBtnEnabled) setIsBtnEnabled(true);
                    }}
                    label={t("seatingCapacity")}
                    placeholder={t("phSeatingCapacity")}
                    title={t("seatingCapacity")}
                    fullWidth
                    error={!!errors.seatingCapacity}
                    helperText={errors.seatingCapacity?.message as string}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    {...register("fleetCode")}
                    label={t("fleetCode")}
                    placeholder={t("phFleetCode")}
                    title={t("fleetCode")}
                    fullWidth
                    error={!!errors.fleetCode}
                    helperText={errors.fleetCode?.message as string}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Controller
                    name="registrationStatus"
                    control={control}
                    defaultValue={null}
                    rules={{ required: false }}
                    render={({ field }) => (
                      <DynamicSelect
                        label={t("registrationStatus")}
                        defaultPlaceholder="phSelectVehicleStatus"
                        {...field}
                        options={registrationStatuses}
                        isSearchable
                        onChange={(selectedOption: any) =>
                          field.onChange(selectedOption)
                        }
                        error={errors.registrationStatus?.message as string}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Controller
                    name="registrationExpiryDate"
                    control={control}
                    rules={{
                      validate: (value) => {
                        const date = new Date(value);
                        return isNaN(date.getTime()) && value
                          ? t("Invalid Date")
                          : true;
                      },
                    }}
                    render={({ field }) => (
                      <BasicDatePicker
                        type="datePicker"
                        {...field}
                        label="registrationExpiryDate"
                        fullWidth
                        onChange={(value: any) =>
                          field.onChange(
                            new Date(value)
                              .toLocaleDateString("en-CA", {
                                year: "numeric",
                                month: "2-digit",
                                day: "2-digit",
                              })
                              ?.split("/")
                              .reverse()
                              .join("-")
                          )
                        }
                        required={false}
                        error={errors.registrationExpiryDate?.message as string}
                        minDate={watch('registrationStatus')?.value === 1 ? dayjs() : ''}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    {...register("tyreInformation")}
                    label={t("tyreInformation")}
                    placeholder={t("phTyreInformation")}
                    title={t("tyreInformation")}
                    fullWidth
                    error={!!errors.tyreInformation}
                    helperText={errors.tyreInformation?.message as string}
                    InputLabelProps={{ shrink: true }}
                    multiline
                    rows={1}
                    sx={{
                      "& .MuiInputBase-fullWidth.MuiInputBase-multiline": {
                        width: "100%",
                        paddingRight: "0px",
                        paddingBottom: 0,
                      },
                      "& .MuiInputBase-inputMultiline": {
                        resize: "vertical",
                        minHeight: "29px",
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    {...register("notes")}
                    label={t("notes")}
                    placeholder={t("phVehicleNotes")}
                    title={t("notes")}
                    fullWidth
                    error={!!errors.notes}
                    helperText={errors.notes?.message as string}
                    InputLabelProps={{ shrink: true }}
                    multiline
                    rows={1}
                    sx={{
                      "& .MuiInputBase-fullWidth.MuiInputBase-multiline": {
                        width: "100%",
                        paddingRight: "0px",
                        paddingBottom: 0,
                      },
                      "& .MuiInputBase-inputMultiline": {
                        resize: "vertical",
                        minHeight: "29px",
                      },
                    }}
                  />
                </Grid>
                <Grid container spacing={2} className="my-[0px] ml-[3px]">
                  <Grid item xs={12} className="px-4">
                    <h1
                      className="flex font-Saira font-[700] text-[17px]"
                      style={{ color: themeColors?.primary }}
                    >
                      {t("insurance")}
                    </h1>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Controller
                      name="insuranceStatus"
                      control={control}
                      defaultValue={null}
                      rules={{ required: false }}
                      render={({ field }) => (
                        <DynamicSelect
                          label={t("insuranceStatus")}
                          defaultPlaceholder="phSelectInsuranceStatus"
                          {...field}
                          options={insuranceStatuses}
                          isSearchable
                          onChange={(selectedOption: any) =>
                            field.onChange(selectedOption)
                          }
                          error={errors.insuranceStatus?.message as string}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Controller
                      name="insuranceExpiryDate"
                      control={control}
                      rules={{
                        validate: (value) => {
                          const date = new Date(value);
                          return isNaN(date.getTime()) && value
                            ? t("Invalid Date")
                            : true;
                        },
                      }}
                      render={({ field }) => (
                        <BasicDatePicker
                          type="datePicker"
                          {...field}
                          label="insuranceExpiryDate"
                          fullWidth
                          onChange={(value: any) =>
                            field.onChange(
                              new Date(value)
                                .toLocaleDateString("en-CA", {
                                  year: "numeric",
                                  month: "2-digit",
                                  day: "2-digit",
                                })
                                ?.split("/")
                                .reverse()
                                .join("-")
                            )
                          }
                          required={false}
                          error={errors.insuranceExpiryDate?.message as string}
                          minDate={watch('insuranceStatus')?.value === 1 ? dayjs() : ''}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Controller
                      name="insuranceCompanyId"
                      control={control}
                      defaultValue={null}
                      rules={{ required: false }}
                      render={({ field }) => (
                        <DynamicSelect
                          label={t("insuranceCompany")}
                          defaultPlaceholder="phSelectInsuranceCompany"
                          {...field}
                          options={insuranceCompanies}
                          isSearchable
                          isClearable
                          isLoading={searchLoading === 'insuranceCompanies'}
                          onChange={(selectedOption: any) =>
                            field.onChange(selectedOption)
                          }
                          onInputChange={(search: string, { action }: { action: string }) => {
                            if(action === 'input-change' || 
                              (action !== 'set-value' && action !== 'menu-close' && action !== 'input-blur')
                            ) handleDropdownActions("insuranceCompanies", 'searchIt', search);
                            else if(action === 'input-blur' || action === 'menu-close') {
                              if(dropdownSearchTerm || pageNo > 1) handleDropdownActions("insuranceCompanies", 'searchIt', '');
                              else setDropdownSearchTerm('');
                              setPageNo(1);
                            }
                          }}
                          onMenuScrollToBottom={() => {if(!(insuranceData?.pages[0]?.data as any).last) handleDropdownActions("insuranceCompanies", 'loadNext')}}
                          error={errors.insuranceCompanyId?.message as string}
                          page={pageNo}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Grid>
            }
          />
        )}
      </Box>
      {updateModal && (
        <FormDialog
          open={updateModal}
          onClose={(event: any, reason: any) => {
            if (event && reason !== "backdropClick") {
              setUpdateModal(false);
              updateVehicle({ ...vehicle, updateImpact: false });
            }
          }}
          hideClose={true}
          title={t("applyChangesToJCWO")}
          content={<Typography>{t(`wantToApplyChanges`)}</Typography>}
          saveFunction={() => {
            setUpdateModal(false);
            updateVehicle(vehicle);
          }}
          submitButtonText="yes"
          cancelBtnText="no"
        />
      )}
    </>
  );
}
